import React, { useState, useEffect } from "react";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import ViewClientPage from "../../../entities/SuperAdminPage/ViewClient";
import { subscriptionService, planService } from "../../../api";
import LoaderHelper from "../../../utils/Loader/LoaderHelper";
import { useAlert } from "../../../context/AlertContext";
import dayjs from "dayjs";

const normalizeTransactionHistory = (source = {}) => {
  const transactionList = Array.isArray(source)
    ? source
    : [
      source?.transactions,
      source?.transactionHistory,
      source?.paymentHistory,
      source?.payments,
      source?.history,
      source?.data?.transactions,
    ].find((item) => Array.isArray(item)) || [];

  return transactionList.map((txn, index) => ({
    id: txn?._id || txn?.id || txn?.reference || txn?.trxref || `txn-${index}`,
    planName: txn?.planId?.planName || txn?.planName || "-",
    billingCycle: txn?.planId?.billingCycle || txn?.billingCycle || "-",
    price: txn?.planId?.price ?? txn?.price ?? "-",
    currency: txn?.planId?.currency || txn?.currency || "",
    date: txn?.date || txn?.createdAt || txn?.paidAt || "",
    status: txn?.status || txn?.paymentStatus || "-",
  }));
};

const extractSubscriptionPayload = (result = {}) => {
  const responseData = result?.data;

  if (responseData?.subscription) {
    return {
      subscription: responseData.subscription,
      transactionHistory: responseData.transactionHistory,
    };
  }

  return {
    subscription: responseData || result,
    transactionHistory: responseData?.transactionHistory || result?.transactionHistory,
  };
};

const ViewClient = () => {
  const [loading, setLoading] = useState(false);
  const [fetching, setFetching] = useState(false);
  const [clientData, setClientData] = useState(null);
  const [transactions, setTransactions] = useState([]);
  const [planList, setPlanList] = useState([]);
  const location = useLocation();
  const navigate = useNavigate();
  const { id } = useParams();
  const { showAlert } = useAlert();

  useEffect(() => {
    const loadData = async () => {
      await fetchPlans();
      const clientId = id || location.state?.clientId || location.pathname.split("/").pop();
      if (clientId) {
        setTimeout(() => {
          fetchClientData(clientId);
        }, 100);
      } else {
        showAlert("Client ID not found", "error");
        navigate("/dashboard/userManagement");
      }
    };
    loadData();
  }, [id]);

  const fetchPlans = async () => {
    try {
      const result = await planService.list();
      const normalizeListResponse = (response) => {
        if (Array.isArray(response)) return response;
        if (Array.isArray(response?.data)) return response.data;
        if (Array.isArray(response?.result)) return response.result;
        return [];
      };
      const plans = normalizeListResponse(result);
      setPlanList(plans || []);
    } catch (error) {
      console.error("Error fetching plans:", error);
    }
  };

  const fetchClientData = async (clientId) => {
    setFetching(true);
    LoaderHelper.loaderStatus(true);
    try {
      const result = await subscriptionService.getById(clientId);
      if (result && !result.error) {
        const { subscription, transactionHistory } = extractSubscriptionPayload(result);
        const company = subscription.companyId || {};
        const admin = subscription.adminId || {};
        const planFromSub =
          subscription?.planId && typeof subscription.planId === "object"
            ? subscription.planId
            : {};
        const subscriptionPlanId =
          typeof subscription?.planId === "string"
            ? subscription.planId
            : subscription?.planId?._id;

        const phoneValue =
          company?.phone?.toString?.() ||
          company?.cellNumber?.toString?.() ||
          admin?.phone?.toString?.() ||
          "";
        const plan =
          planFromSub._id
            ? planFromSub
            : planList.find(
              (p) => p._id === subscriptionPlanId || p._id === subscription?.subscriptionId,
            ) || {};

        const parsedStartDate = dayjs(subscription?.date);

        const loadedData = {
          companyName: company.companyName || "",
          companyMailingAddress: company.email || "",
          address1: company.address1 || "",
          owner: company.owner || `${admin.firstName || ""} ${admin.lastName || ""}`.trim() || "",
          phoneNumber: phoneValue,
          city: company.city || "",
          state: company.state || "",
          zipCode: company.zipCode || "",
          country: company.country || "",
          industry: company.industry || "",
          plan: plan._id || subscriptionPlanId || planFromSub._id || "",
          planName: plan.planName || planFromSub.planName || subscription?.planName || "",
          billingCycle: planFromSub.billingCycle || plan.billingCycle || subscription?.billingCycle || "",
          startDate:
            subscription?.date && parsedStartDate.isValid()
              ? parsedStartDate
              : dayjs(),
          adminEmail: admin.email || "",
          isActive: subscription.isActive !== undefined ? subscription.isActive : true,
          planPrice: subscription.planPrice || planFromSub.price || "",
          planCurrency: subscription.planCurrency || "",
          planExpiryDate: (subscription.planExpiryDate || subscription.overrides?.planExpiryDate || subscription.nextBillingDate) ? dayjs(subscription.planExpiryDate || subscription.overrides?.planExpiryDate || subscription.nextBillingDate) : null,
          moduleAccessOverrides: subscription.overrides?.moduleAccessOverrides || subscription.moduleAccessOverrides || {},
          moduleLimitOverrides: subscription.overrides?.moduleLimitOverrides || subscription.moduleLimitOverrides || {},
          effectiveAccess: result.data?.effectiveAccess || {},
          effectiveLimits: result.data?.effectiveLimits || {},
          usage: result.data?.usage || {},
          moduleAccess: planFromSub.moduleAccess || {},
          moduleLimits: planFromSub.moduleLimits || {},
        };

        setClientData(loadedData);
        setTransactions(normalizeTransactionHistory(transactionHistory || subscription));
      } else {
        showAlert(result?.message || "Failed to load client data", "error");
        navigate("/dashboard/userManagement");
      }
      setFetching(false);
      LoaderHelper.loaderStatus(false);
    } catch (error) {
      setFetching(false);
      LoaderHelper.loaderStatus(false);
      showAlert(error?.message || "Error loading client data", "error");
      navigate("/dashboard/userManagement");
    }
  };

  return (
    <ViewClientPage
      clientData={clientData}
      transactions={transactions}
    />
  );
};

export default ViewClient;
